// SmoothingUtils.ts
export interface Point {
  x: number;
  y: number;
}

// Exponential Moving Average for smoothing
export const applySmoothing = (currentPoint: Point, previousPoint: Point | null, alpha: number = 0.3): Point => {
  if (!previousPoint) {
    return currentPoint;
  }
  
  return {
    x: alpha * currentPoint.x + (1 - alpha) * previousPoint.x,
    y: alpha * currentPoint.y + (1 - alpha) * previousPoint.y
  };
};

// Calculate distance between two points
export const distance = (p1: Point, p2: Point): number => {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
};

// Calculate speed based on distance and time
export const calculateSpeed = (currentPoint: Point, previousPoint: Point | null, deltaTime: number): number => {
  if (!previousPoint || deltaTime <= 0) {
    return 0;
  }
  
  const dist = distance(currentPoint, previousPoint);
  return dist / deltaTime;
};

// Adjust brush size based on speed (faster = thinner lines)
export const adjustBrushSizeForSpeed = (
  baseSize: number,
  speed: number,
  minSize: number = 1,
  maxSize: number = 60
): number => {
  // Map speed (px/sec) to a gentle factor: faster => thinner
  // Assume common speeds 0..800 px/s; clamp beyond
  const normalized = Math.max(0, Math.min(1, speed / 800));
  const factor = 1 - 0.4 * normalized; // 1.0 .. 0.6
  const adjustedSize = baseSize * factor;
  return Math.max(minSize, Math.min(maxSize, adjustedSize));
};

// One Euro Filter for low-latency smoothing
export class OneEuroFilter {
  private freq: number; // expected update rate (Hz)
  private minCutoff: number;
  private beta: number;
  private dCutoff: number;
  private xPrev: number | null = null;
  private dxPrev: number | null = null;
  private lastTime: number | null = null;

  constructor(freq = 60, minCutoff = 1.0, beta = 0.0, dCutoff = 1.0) {
    this.freq = freq;
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dCutoff = dCutoff;
  }

  private alpha(cutoff: number, dt: number) {
    const tau = 1.0 / (2 * Math.PI * cutoff);
    return 1.0 / (1.0 + tau / dt);
  }

  filter(value: number, timestampMs: number): number {
    const t = timestampMs / 1000;
    let dt = 1.0 / this.freq;
    if (this.lastTime != null) {
      dt = Math.max(1e-3, t - this.lastTime);
    }
    this.lastTime = t;

    let dx = 0;
    if (this.xPrev != null) {
      dx = (value - this.xPrev) / dt;
    }
    const aD = this.alpha(this.dCutoff, dt);
    const dxHat = this.dxPrev == null ? dx : aD * dx + (1 - aD) * this.dxPrev;
    this.dxPrev = dxHat;

    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const a = this.alpha(cutoff, dt);
    const xHat = this.xPrev == null ? value : a * value + (1 - a) * this.xPrev;
    this.xPrev = xHat;
    return xHat;
  }
}

export class OneEuroFilter2D {
  private fx: OneEuroFilter;
  private fy: OneEuroFilter;
  constructor(freq = 60, minCutoff = 1.0, beta = 0.0, dCutoff = 1.0) {
    this.fx = new OneEuroFilter(freq, minCutoff, beta, dCutoff);
    this.fy = new OneEuroFilter(freq, minCutoff, beta, dCutoff);
  }
  filter(p: Point, timestampMs: number): Point {
    return { x: this.fx.filter(p.x, timestampMs), y: this.fy.filter(p.y, timestampMs) };
  }
}